import React, { Component, ErrorInfo, ReactNode } from 'react';
import { motion } from 'framer-motion';
import { VaultKeTheme } from '../utils/theme';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
}

class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
  };

  public static getDerivedStateFromError(_: Error): State {
    return { hasError: true };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('Uncaught error:', error, errorInfo);
  }

  public render() {
    if (this.state.hasError) {
      const styles: { [key: string]: React.CSSProperties } = {
        container: {
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: VaultKeTheme.colors.background,
          color: VaultKeTheme.colors.text,
          padding: VaultKeTheme.spacing.lg,
          textAlign: 'center',
        },
        title: {
          ...VaultKeTheme.typography.h1,
          color: VaultKeTheme.colors.error,
          marginBottom: VaultKeTheme.spacing.lg,
        },
        message: {
          ...VaultKeTheme.typography.body1,
          marginBottom: VaultKeTheme.spacing.xl,
          maxWidth: '600px',
        },
        button: {
          ...VaultKeTheme.components.button.primary,
          padding: '12px 24px',
          cursor: 'pointer',
        },
      };

      const handleReload = () => {
        window.location.reload();
      };

      const containerVariants = {
        hidden: { opacity: 0, y: 20 },
        visible: {
          opacity: 1,
          y: 0,
          transition: {
            duration: 0.6,
            staggerChildren: 0.2,
          },
        },
      };

      const itemVariants = {
        hidden: { opacity: 0, y: 20 },
        visible: {
          opacity: 1,
          y: 0,
          transition: {
            duration: 0.6,
          },
        },
      };

      return (
        <motion.div
          style={styles.container}
          initial="hidden"
          animate="visible"
          variants={containerVariants}
        >
          <motion.div variants={itemVariants}>
            <motion.h1 style={styles.title} variants={itemVariants}>
              Oops! Something went wrong
            </motion.h1>
            <motion.p style={styles.message} variants={itemVariants}>
              We're sorry, but an unexpected error occurred. Please try refreshing the page.
              If the problem persists, contact our support team.
            </motion.p>
            <motion.div
              variants={itemVariants}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <button
                style={styles.button}
                onClick={handleReload}
              >
                Refresh Page
              </button>
            </motion.div>
          </motion.div>
        </motion.div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;